import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Clock } from "lucide-react";
import { api, absUploadUrl } from "../lib/apiClient";
import { useI18n } from "../i18n/I18nProvider";
import { offerDetailPath } from "../lib/offerUrl";

export default function OffersList() {
  const { t, locale } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/offers?locale=${locale}`);
        setItems(data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [locale]);

  return (
    <div data-testid="offers-list-page" className="min-h-[60vh]">
      {/* Hero */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="container-oj py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-oj-brand font-semibold flex items-center gap-2">
              <Sparkles className="size-3.5" /> {t("offers.eyebrow")}
            </div>
            <h1 className="mt-3 font-display font-black tracking-tighter text-oj-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              {t("offers.h1")}
            </h1>
            <p className="mt-5 text-lg text-zinc-600 max-w-2xl">
              {t("offers.intro")}
            </p>
          </div>
        </div>
      </section>

      {/* List */}
      <section className="bg-zinc-50">
        <div className="container-oj py-16">
          {loading ? (
            <div className="text-sm text-zinc-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-zinc-200 bg-white p-12 text-center">
              <div className="text-lg font-semibold text-oj-ink">{t("offers.empty_title")}</div>
              <p className="mt-2 text-sm text-zinc-500">{t("offers.empty_body")}</p>
              <Link
                to="/#demo"
                className="mt-6 inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-4 py-2.5 rounded-md"
              >
                {t("nav.demo")} <ArrowUpRight className="size-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.map((o) => (
                <OfferCard key={o.id} offer={o} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function OfferCard({ offer }) {
  const { t, locale } = useI18n();
  const daysLeft = offer.valid_until
    ? Math.max(0, Math.ceil((new Date(offer.valid_until) - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const href = offerDetailPath(offer, locale);
  return (
    <Link
      to={href}
      data-testid={`offer-card-${offer.slug}`}
      className="group block rounded-2xl border border-zinc-200 bg-white overflow-hidden hover:border-oj-ink transition"
    >
      <div className="relative aspect-[1200/630] bg-zinc-100 overflow-hidden">
        {offer.list_image_url ? (
          <img
            src={absUploadUrl(offer.list_image_url)}
            alt={offer.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-oj-brand/25 to-oj-deep/10" />
        )}
        {offer.discount_label && (
          <div className="absolute top-3 left-3 bg-oj-ink text-white text-xs font-bold px-2.5 py-1 rounded-full">
            {offer.discount_label}
          </div>
        )}
        {daysLeft !== null && daysLeft <= 30 && daysLeft > 0 && (
          <div className="absolute top-3 right-3 bg-white/95 text-oj-ink text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1">
            <Clock className="size-3" /> {t("offers.days_left").replace("{n}", daysLeft)}
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-xl font-display font-bold tracking-tight text-oj-ink group-hover:text-oj-brand transition-colors">
          {offer.title}
        </h3>
        {offer.subtitle && (
          <p className="mt-1.5 text-sm text-zinc-600 line-clamp-2">{offer.subtitle}</p>
        )}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm font-semibold text-oj-ink">
            {offer.price > 0 ? `${offer.currency} ${offer.price}` : t("offers.free")}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-oj-brand">
            {t("offers.view")} <ArrowUpRight className="size-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
